export type SidebarSectionId = "home" | "assets" | "processing" | "governance" | "requests" | "clearing-house";

const SIDEBAR_ACCORDION_SECTION_IDS = ["assets", "processing", "governance", "requests"] as const;

export type SidebarAccordionSectionId = (typeof SIDEBAR_ACCORDION_SECTION_IDS)[number];
export type NavigationSectionPlacement = "sidebar" | "topbar";

export type SidebarNavigationItem = {
    id: string | number;
    name: string;
    title: string;
    group: string;
    route: string;
    visible: boolean;
    selectedPages?: string[];
};

export type SidebarNavigationSection = {
    id: SidebarSectionId;
    title: string;
    icon: string;
    visible: boolean;
    placements: NavigationSectionPlacement[];
    route?: string;
    selectedPages?: string[];
    items?: SidebarNavigationItem[];
};

export type SidebarNavigationContext = {
    $t: (key: string) => string;
    isAuthenticated: boolean;
    hasPermission: (permission: string) => boolean;
    clearingHouseEnabled: boolean;
};

function hasAnyPermission(ctx: SidebarNavigationContext, permissions: string[]): boolean {
    return permissions.some((permission) => ctx.hasPermission(permission));
}

export function buildSidebarNavigation(ctx: SidebarNavigationContext): SidebarNavigationSection[] {
    const canDataSources = ctx.hasPermission("sources.read");
    const canDataSinks = ctx.hasPermission("sinks.read");
    const canDataApps = ctx.hasPermission("apps.read");
    const canPipelines = ctx.hasPermission("pipelines.read");
    const canReadCatalogs = ctx.hasPermission("catalog.read");
    const canReadNegotiations = ctx.hasPermission("negotiation.read");
    const canReadTransfers = ctx.hasPermission("transfer.read");
    const canReadClearingHouse = ctx.clearingHouseEnabled && ctx.hasPermission("clearing.house.read");
    const canCatalogBrokerRequests = ctx.hasPermission("catalog.broker.requests");
    const canAttestationRequests = hasAnyPermission(ctx, ["attestation.requests", "attestation.revocation"]);

    return [
        {
            id: "home",
            title: ctx.$t("Home"),
            icon: "fas fa-home",
            route: "home",
            visible: ctx.isAuthenticated,
            placements: ["sidebar", "topbar"],
            selectedPages: ["home"],
        },
        {
            id: "assets",
            title: ctx.$t("Assets"),
            icon: "fas fa-database",
            visible: true,
            placements: ["sidebar", "topbar"],
            items: [
                {
                    id: 1,
                    name: "data-sources",
                    title: ctx.$t("Data sources"),
                    group: "data-assets",
                    route: "data-sources",
                    visible: canDataSources,
                    selectedPages: ["data-sources", "data-source", "data-sources-create"],
                },
                {
                    id: 2,
                    name: "data-sinks",
                    title: ctx.$t("Data sinks"),
                    group: "data-assets",
                    route: "data-sinks",
                    visible: canDataSinks,
                    selectedPages: ["data-sinks", "data-sink", "data-sinks-create"],
                },
                {
                    id: 3,
                    name: "catalogs",
                    title: ctx.$t("Catalogs"),
                    group: "data-assets",
                    route: "catalogs",
                    visible: canReadCatalogs,
                    selectedPages: ["catalogs", "catalog", "catalog-create"],
                },
                {
                    id: 4,
                    name: "datasets",
                    title: ctx.$t("Datasets"),
                    group: "data-assets",
                    route: "datasets",
                    visible: canReadCatalogs,
                    selectedPages: ["datasets", "dataset", "dataset-create"],
                },
            ],
        },
        {
            id: "processing",
            title: ctx.$t("Processing"),
            icon: "fas fa-address-card",
            visible: true,
            placements: ["sidebar", "topbar"],
            items: [
                {
                    id: 1,
                    name: "data-apps",
                    title: ctx.$t("Data applications"),
                    group: "data-processing",
                    route: "data-apps",
                    visible: canDataApps,
                    selectedPages: ["data-apps", "data-app", "data-apps-create"],
                },
                {
                    id: 2,
                    name: "pipelines",
                    title: ctx.$t("Data pipelines"),
                    group: "data-processing",
                    route: "pipelines",
                    visible: canPipelines,
                    selectedPages: ["pipelines", "pipeline", "pipeline-create", "execution", "analysis-result"],
                },
            ],
        },
        {
            id: "governance",
            title: ctx.$t("Governance"),
            icon: "fas fa-gavel",
            visible: true,
            placements: ["sidebar", "topbar"],
            items: [
                {
                    id: 1,
                    name: "negotiations",
                    title: ctx.$t("Negotiations"),
                    group: "data-trading",
                    route: "negotiations",
                    visible: canReadNegotiations,
                    selectedPages: ["negotiations", "negotiation", "negotiation-create", "negotiation-offer", "negotiation-request"],
                },
                {
                    id: 2,
                    name: "agreements",
                    title: ctx.$t("Agreements"),
                    group: "data-governance",
                    route: "agreements",
                    visible: canReadNegotiations,
                    selectedPages: ["agreements", "agreement"],
                },
                {
                    id: 3,
                    name: "transfers",
                    title: ctx.$t("Transfers"),
                    group: "data-trading",
                    route: "transfers",
                    visible: canReadTransfers,
                    selectedPages: ["transfers", "transfer", "transfer-create"],
                },
                {
                    id: 5,
                    name: "negotiation-automation",
                    title: ctx.$t("Negotiation Automation"),
                    group: "data-trading",
                    route: "negotiation-automation-policies",
                    visible: canReadNegotiations,
                    selectedPages: [
                        "negotiation-automation-policies",
                        "negotiation-automation-policy",
                        "negotiation-automation-policy-create",
                    ],
                },
            ],
        },
        {
            id: "requests",
            title: ctx.$t("Requests"),
            icon: "fas fa-inbox",
            visible: true,
            placements: ["sidebar", "topbar"],
            items: [
                {
                    id: 1,
                    name: "broker-requests",
                    title: ctx.$t("Broker registration"),
                    group: "data-requests",
                    route: "broker-requests",
                    visible: canCatalogBrokerRequests,
                    selectedPages: ["broker-requests", "broker-request", "provider-request"],
                },
                {
                    id: 2,
                    name: "attestation-requests",
                    title: ctx.$t("Attestation issuance"),
                    group: "data-requests",
                    route: "attestation-requests",
                    visible: canAttestationRequests,
                    selectedPages: ["attestation-requests", "attestation-request-sent", "attestation-request-received"],
                },
            ],
        },
        {
            id: "clearing-house",
            title: ctx.$t("Clearing house"),
            icon: "fas fa-scale-balanced",
            route: "clearing-house",
            visible: canReadClearingHouse,
            placements: ["sidebar", "topbar"],
            selectedPages: ["clearing-house", "clearing-house-agreement", "clearing-house-transfer"],
        },
    ];
}

function isRouteInSelectedPages(selectedPages: string[] | undefined, page: string): boolean {
    return Boolean(selectedPages?.includes(page));
}

export function isRouteInNavigationItem(item: SidebarNavigationItem, page: string): boolean {
    return item.selectedPages?.includes(page) || item.route === page;
}

export function isRouteInNavigationSection(section: SidebarNavigationSection, page: string): boolean {
    return section.route === page || isRouteInSelectedPages(section.selectedPages, page);
}

export function getActiveSidebarSection(page: string, sections: SidebarNavigationSection[]): SidebarNavigationSection | null {
    return (
        sections.find((section) => {
            if (!section.visible) return false;
            if (isRouteInNavigationSection(section, page)) return true;

            return section.items?.some((item) => item.visible && isRouteInNavigationItem(item, page));
        }) || null
    );
}

export function getActiveSidebarItem(page: string, sections: SidebarNavigationSection[]): SidebarNavigationItem | null {
    for (const section of sections) {
        if (!section.visible || !section.items) continue;

        const item = section.items.find((navigationItem) => navigationItem.visible && isRouteInNavigationItem(navigationItem, page));
        if (item) return item;
    }

    return null;
}

export function getSidebarSectionById(sections: SidebarNavigationSection[], sectionId: SidebarSectionId): SidebarNavigationSection | null {
    return sections.find((section) => section.id === sectionId) || null;
}

export function getSidebarSectionItems(sections: SidebarNavigationSection[], sectionId: SidebarSectionId): SidebarNavigationItem[] {
    return getSidebarSectionById(sections, sectionId)?.items || [];
}

export function isSidebarNavigationSection(section: SidebarNavigationSection): boolean {
    return section.placements.includes("sidebar");
}

export function getSidebarNavigationSections(sections: SidebarNavigationSection[]): SidebarNavigationSection[] {
    return sections.filter(isSidebarNavigationSection);
}

export function isSidebarAccordionSectionId(sectionId: SidebarSectionId): sectionId is SidebarAccordionSectionId {
    return (SIDEBAR_ACCORDION_SECTION_IDS as readonly SidebarSectionId[]).includes(sectionId);
}
